import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PAGE_TITLES, ini } from '@cls/shared';
import { useTheme } from '../../features/theme/useTheme.js';
import { useAuth } from '../../features/auth/AuthProvider.js';
import { useRolePerms } from '../../features/auth/useRolePerms.js';
import { NotificationBell } from './NotificationBell.js';
import { GlobalSearch } from './GlobalSearch.js';
import { useNavHistory } from '../../features/history/useHistory.js';
import { useFilterReset } from '../../features/filters/useFilters.js';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';

interface Props {
  onMenuToggle: () => void;
}

interface CertCounts {
  expiredCerts?: number;
  expiringCerts?: number;
}

export function TopBar({ onMenuToggle }: Props) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const { isSiteUser } = useRolePerms();
  const qc = useQueryClient();
  const { canBack, canForward, back, forward } = useNavHistory();
  const { bumpReset } = useFilterReset();

  const pageKey = pathname.split('/').filter(Boolean)[0] ?? 'dashboard';
  const title = PAGE_TITLES[pageKey] ?? 'CLS Command Centre';

  const { data: kpis } = useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: () => api.get<CertCounts>('/dashboard/kpis').then((r) => r.data),
    refetchInterval: 30_000,
    enabled: !isSiteUser,
  });
  const certAlertCount = (kpis?.expiredCerts ?? 0) + (kpis?.expiringCerts ?? 0);

  function resetView() {
    bumpReset();
    qc.invalidateQueries();
    toast.info('View Reset', 'All filters cleared');
  }

  return (
    <div id="topbar">
      <button id="menu-btn" onClick={onMenuToggle}>☰</button>
      <button
        className="page-nav-btn"
        id="nav-back-btn"
        onClick={back}
        disabled={!canBack}
        title="Back"
      >
        ‹
      </button>
      <button
        className="page-nav-btn"
        id="nav-fwd-btn"
        onClick={forward}
        disabled={!canForward}
        title="Forward"
      >
        ›
      </button>
      <div id="page-title">{title}</div>
      {!isSiteUser && <GlobalSearch />}
      <button
        className="btn btn-ghost btn-sm"
        onClick={resetView}
        title="Reset view / clear all filters"
        style={{ whiteSpace: 'nowrap', fontSize: 11 }}
      >
        ↺ Reset
      </button>
      <div
        className="alert-chip"
        id="cert-chip"
        style={{ display: certAlertCount > 0 ? 'block' : 'none' }}
        onClick={() => navigate('/training')}
      >
        ⚠ {certAlertCount} cert alerts
      </div>
      <NotificationBell />
      <button
        id="theme-btn"
        onClick={() => { toggle(); toast.info('Theme Switched', theme === 'dark' ? 'Light mode' : 'Dark mode'); }}
        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontSize: 13, color: 'var(--muted)', transition: 'border-color .15s' }}
        title="Toggle light/dark mode"
      >
        {theme === 'dark' ? '🌙' : '☀️'}
      </button>
      <button
        id="logout-btn"
        onClick={async () => {
          await logout();
          toast.info('Logged out', 'You have been signed out');
        }}
        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px', cursor: 'pointer', fontSize: 13, color: 'var(--muted)', transition: 'border-color .15s', whiteSpace: 'nowrap' }}
        title="Log out"
      >
        ⎋ Log out
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <div style={{ textAlign: 'right', lineHeight: 1.2 }}>
          <div style={{ fontSize: 8, color: 'var(--faint)' }}>Role</div>
          <div id="current-role-label" style={{ fontSize: 9, color: 'var(--accent)', fontWeight: 600 }}>
            {user?.role ?? ''}
          </div>
        </div>
        <div className="user-av" id="user-av-btn">{user?.name ? ini(user.name) : '??'}</div>
      </div>
    </div>
  );
}
